import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Phone, Car, MapPin, Crosshair, FileText, Camera, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, SERVICE_TYPES, VEHICLE_TYPES } from "@/lib/api";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { roadDistanceKm } from "@/lib/geo";

export default function RequestForm({ compact = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [data, setData] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    service_type: "",
    vehicle_type: "",
    vehicle_brand: "",
    vehicle_model: "",
    vehicle_plate: "",
    pickup_address: "",
    pickup_lat: null,
    pickup_lng: null,
    dropoff_address: "",
    description: "",
    urgency: "immediat",
    preferred_contact: "phone",
  });
  const [dropoffCoords, setDropoffCoords] = useState(null);

  const setField = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  // Recalcule la distance routière dès que départ + arrivée sont géolocalisés
  const recalcDistance = (pickup, dropoff) => {
    const km = roadDistanceKm(pickup, dropoff);
    if (km != null) setData((d) => ({ ...d, distance_km: km }));
  };

  const onPickupSelect = (f) => {
    if (!f) return setData((d) => ({ ...d, pickup_lat: null, pickup_lng: null }));
    setData((d) => ({ ...d, pickup_address: f.label, pickup_lat: f.lat, pickup_lng: f.lng }));
    recalcDistance({ lat: f.lat, lng: f.lng }, dropoffCoords);
  };

  const onDropoffSelect = (f) => {
    if (!f) return setDropoffCoords(null);
    setData((d) => ({ ...d, dropoff_address: f.label }));
    setDropoffCoords({ lat: f.lat, lng: f.lng });
    if (data.pickup_lat != null) recalcDistance({ lat: data.pickup_lat, lng: data.pickup_lng }, { lat: f.lat, lng: f.lng });
  };

  const useGPS = () => {
    if (!navigator.geolocation) return toast.error("Géolocalisation non supportée");
    toast.loading("Localisation en cours...", { id: "gps" });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const addr = `Position GPS: ${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
        setData((d) => ({ ...d, pickup_address: addr, pickup_lat: latitude, pickup_lng: longitude }));
        recalcDistance({ lat: latitude, lng: longitude }, dropoffCoords);
        toast.success("Position GPS récupérée", { id: "gps" });
      },
      () => toast.error("Impossible d'obtenir votre position", { id: "gps" })
    );
  };

  const onPhotos = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4);
    Promise.all(files.map((f) => fileToBase64(f))).then((arr) => setPhotos(arr));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!data.customer_name || !data.customer_phone || !data.service_type || !data.pickup_address) {
      toast.error("Merci de remplir les champs obligatoires");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/requests", { ...data, photos });
      toast.success(`Demande créée : ${res.data.request_number}`);
      navigate(`/demande/confirmation/${res.data.request_number}`);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full h-12 px-4 bg-surface-50 border border-surface-200 rounded-xl text-txt-900 text-sm placeholder:text-txt-300 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-all";

  return (
    <form onSubmit={submit} className={`${compact ? "p-5" : "p-6 sm:p-8"}`} data-testid="request-form">
      {!compact && (
        <div className="mb-6">
          <h3 className="text-xl sm:text-2xl font-extrabold text-txt-900">Demande de dépannage</h3>
          <p className="text-txt-500 text-sm mt-1">Remplissez le formulaire, nous vous rappelons immédiatement</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="relative">
          <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-txt-300" />
          <input className={inputCls} style={{ paddingLeft: 40 }} placeholder="Votre nom *" value={data.customer_name} onChange={setField("customer_name")} data-testid="rf-name" />
        </div>
        <div className="relative">
          <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-txt-300" />
          <input className={inputCls} style={{ paddingLeft: 40 }} type="tel" placeholder="Votre téléphone *" value={data.customer_phone} onChange={setField("customer_phone")} data-testid="rf-phone" />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <div className="relative">
          <Car size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-txt-300 pointer-events-none" />
          <select className={`${inputCls} appearance-none`} style={{ paddingLeft: 40, paddingRight: 32 }} value={data.service_type} onChange={setField("service_type")} data-testid="rf-service">
            <option value="">Type de service *</option>
            {SERVICE_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-txt-300 pointer-events-none text-xs">&#9662;</span>
        </div>
        <div className="relative">
          <Car size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-txt-300 pointer-events-none" />
          <select className={`${inputCls} appearance-none`} style={{ paddingLeft: 40, paddingRight: 32 }} value={data.vehicle_type} onChange={setField("vehicle_type")} data-testid="rf-vehicle-type">
            <option value="">Type de véhicule</option>
            {VEHICLE_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-txt-300 pointer-events-none text-xs">&#9662;</span>
        </div>
      </div>

      <div className="mt-3">
        <AddressAutocomplete
          value={data.pickup_address}
          onChange={(t) => setData((d) => ({ ...d, pickup_address: t }))}
          onSelect={onPickupSelect}
          placeholder="Adresse d'intervention *"
          inputClassName={inputCls}
          inputStyle={{ paddingLeft: 40, paddingRight: 100 }}
          testid="rf-pickup"
          leftIcon={<MapPin size={16} className="absolute left-3.5 top-6 -translate-y-1/2 text-txt-300 z-10" />}
          rightSlot={
            <button
              type="button"
              onClick={useGPS}
              className="absolute right-1.5 top-1.5 h-9 px-3 bg-brand-50 text-brand rounded-lg flex items-center justify-center gap-1.5 hover:bg-brand-100 transition-colors z-10"
              data-testid="rf-gps-btn"
              aria-label="Utiliser ma position GPS"
              title="Utiliser ma position GPS"
            >
              <Crosshair size={14} />
              <span className="hidden sm:inline text-xs font-bold">GPS</span>
            </button>
          }
        />
      </div>

      {!compact && (
        <>
          <div className="mt-3">
            <AddressAutocomplete
              value={data.dropoff_address}
              onChange={(t) => setData((d) => ({ ...d, dropoff_address: t }))}
              onSelect={onDropoffSelect}
              placeholder="Destination (optionnel)"
              inputClassName={inputCls}
              inputStyle={{ paddingLeft: 40 }}
              testid="rf-dropoff"
              leftIcon={<MapPin size={16} className="absolute left-3.5 top-6 -translate-y-1/2 text-txt-300 z-10" />}
            />
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mt-3">
            <input className={inputCls} placeholder="Marque" value={data.vehicle_brand} onChange={setField("vehicle_brand")} data-testid="rf-brand" />
            <input className={inputCls} placeholder="Modèle" value={data.vehicle_model} onChange={setField("vehicle_model")} data-testid="rf-model" />
            <input className={inputCls} placeholder="Immatriculation" value={data.vehicle_plate} onChange={setField("vehicle_plate")} data-testid="rf-plate" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <select className={`${inputCls} appearance-none`} value={data.urgency} onChange={setField("urgency")} data-testid="rf-urgency">
              <option value="immediat">Urgence : Immédiat</option>
              <option value="aujourd_hui">Aujourd'hui</option>
              <option value="rendez_vous">Sur rendez-vous</option>
            </select>
            <select className={`${inputCls} appearance-none`} value={data.preferred_contact} onChange={setField("preferred_contact")} data-testid="rf-contact">
              <option value="phone">Contact : Téléphone</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="email">Email</option>
            </select>
          </div>
        </>
      )}

      <div className="mt-3">
        <div className="relative">
          <FileText size={16} className="absolute left-3.5 top-3.5 text-txt-300" />
          <textarea
            className={`${inputCls} min-h-[72px] h-auto py-3 resize-y`}
            style={{ paddingLeft: 40 }}
            placeholder="Description de la panne ou demande"
            value={data.description}
            onChange={setField("description")}
            data-testid="rf-description"
          />
        </div>
      </div>

      <div className="mt-3">
        <label className={`${inputCls} flex items-center justify-between gap-2 cursor-pointer`} data-testid="rf-photos-label">
          <span className="flex items-center gap-2 text-txt-300 min-w-0 flex-1 truncate">
            <Camera size={16} className="shrink-0" />
            <span className="truncate">{photos.length ? `${photos.length} photo(s) ajoutée(s)` : "Photos (optionnel)"}</span>
          </span>
          <span className="shrink-0 bg-brand-50 text-brand text-xs font-bold px-3 py-1 rounded-lg">Ajouter</span>
          <input type="file" accept="image/*" multiple onChange={onPhotos} className="hidden" />
        </label>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="mt-5 w-full bg-brand hover:bg-brand-600 text-white font-bold rounded-xl text-base py-4 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
        data-testid="rf-submit"
      >
        {loading ? "Envoi en cours..." : "Envoyer ma demande"} <ArrowRight size={18} />
      </button>
      <div className="mt-3 flex items-center justify-center gap-2 text-xs text-txt-500">
        <ShieldCheck size={14} className="text-brand" /> Rappel dans les plus brefs délais
      </div>
    </form>
  );
}

function fileToBase64(f) {
  return new Promise((res) => {
    const r = new FileReader();
    r.onloadend = () => res(r.result);
    r.readAsDataURL(f);
  });
}
